import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { a as createLogger, t as throttle, e as emitEvent, j as hsvToHex, k as hsvToRgb, m as isValidHex, o as hexToRgb, p as hexToHsv, d as isNumber, b as clamp, r as rgbToHex, q as isValidRgb, s as rgbToHsv, l as logDeprecatedEventOnce } from './utils.js';
import { d as defineCustomElement$2 } from './cl-typography2.js';

// Constants
const component = 'cl-color-picker';

const clColorPickerCss = ".sc-cl-color-picker-h{--color-picker-z-index:var(--z-index-5);background-color:rgba(var(--color-neutral-main));border-radius:var(--border-radius-interactive-large);box-shadow:var(--elevation-2);height:calc(var(--spacing-1) * 31.5);position:absolute;width:calc(var(--spacing-1) * 29);z-index:var(--color-picker-z-index)}.sc-cl-color-picker-h,.sc-cl-color-picker-h *.sc-cl-color-picker,.sc-cl-color-picker-h *.sc-cl-color-picker::before,.sc-cl-color-picker-h *.sc-cl-color-picker::after,.sc-cl-color-picker-h .sc-cl-color-picker-s>*,.sc-cl-color-picker-h .sc-cl-color-picker-s *,.sc-cl-color-picker-h .sc-cl-color-picker-s *::before,.sc-cl-color-picker-h .sc-cl-color-picker-s *::after{box-sizing:border-box}.sc-cl-color-picker-h .color-picker__content.sc-cl-color-picker{height:100%;padding:var(--spacing-2);position:relative;width:100%}.color-picker--opening.sc-cl-color-picker-h{animation:expand 150ms var(--animation-timing-easeinout)}.color-picker--opened.sc-cl-color-picker-h{opacity:var(--opacity-5, 1)}.color-picker--closing.sc-cl-color-picker-h{animation:collapse 150ms var(--animation-timing-easeinout)}.color-picker--closed.sc-cl-color-picker-h{display:none;opacity:var(--opacity-0, 0)}.sc-cl-color-picker-h input.color-picker__color-input.sc-cl-color-picker{--picker-hue:360;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:linear-gradient(to right, rgb(255, 0, 0) 3.6%, rgb(255, 255, 0), rgb(0, 255, 0), rgb(0, 255, 255), rgb(0, 0, 255), rgb(255, 0, 255), rgb(255, 0, 0) 96.4%);border:var(--spacing-quarter);border-color:rgba(var(--color-neutral-50));border-radius:var(--border-radius-interactive-small);color:rgba(var(--color-brand-main));cursor:pointer;height:calc(var(--spacing-2) - var(--spacing-half));left:calc(var(--spacing-1) * -7.75);outline:none;position:absolute;top:calc(var(--spacing-1) * 11.5);transform:rotate(270deg);width:calc(var(--spacing-1) * 20.5);writing-mode:bt-lr}.sc-cl-color-picker-h input.color-picker__color-input.sc-cl-color-picker::-moz-range-track{margin:0 calc(-1 * var(--spacing-quarter))}.sc-cl-color-picker-h input.color-picker__color-input.sc-cl-color-picker::-webkit-slider-runnable-track{margin:0 calc(-1 * var(--spacing-quarter))}.sc-cl-color-picker-h input.color-picker__color-input.sc-cl-color-picker::-moz-range-thumb{-moz-appearance:none;appearance:none;background-color:hsl(var(--picker-hue), 100%, 50%);border:var(--spacing-quarter) solid rgba(var(--color-neutral-50s));border-radius:var(--border-radius-circle);box-shadow:var(--elevation-x-2) calc(var(--elevation-y-2) - var(--spacing-1) - var(--spacing-quarter)) var(--elevation-blur-2) var(--elevation-spread-2) rgba(var(--elevation-color-2));cursor:grab;height:var(--spacing-2);overflow:visible;width:var(--spacing-2)}.sc-cl-color-picker-h input.color-picker__color-input.sc-cl-color-picker::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;background-color:hsl(var(--picker-hue), 100%, 50%);border:var(--spacing-quarter) solid rgba(var(--color-neutral-50s));border-radius:var(--border-radius-circle);box-shadow:var(--elevation-x-2) calc(var(--elevation-y-2) - var(--spacing-1) - var(--spacing-quarter)) var(--elevation-blur-2) var(--elevation-spread-2) rgba(var(--elevation-color-2));cursor:grab;height:var(--spacing-2);overflow:visible;width:var(--spacing-2)}.sc-cl-color-picker-h div.color-picker__gradient-selector.sc-cl-color-picker{--picker-hue:360;--gradient-pos-x:100%;--gradient-pos-y:0%;--gradient-picker-size:calc(var(--spacing-1) * 21);background:linear-gradient(to top, rgb(0, 0, 0) 6%, rgba(0, 0, 0, 0) 94%), linear-gradient(to right, rgb(255, 255, 255) 6%, hsl(var(--picker-hue), 100%, 50%) 94%);border:var(--spacing-quarter);border:#fff;border-radius:var(--border-radius-interactive-small);cursor:pointer;height:var(--gradient-picker-size);position:absolute;right:var(--spacing-2);width:var(--gradient-picker-size)}.sc-cl-color-picker-h div.color-picker__gradient-selector.sc-cl-color-picker div.color-picker__gradient-selector__thumb.sc-cl-color-picker{--thumb-pos-x:1;--thumb-pos-y:0;--thumb-colour:rgb(255, 0, 0);--thumb-size:calc(var(--spacing-1) + var(--spacing-half));background-color:var(--thumb-colour);border:var(--spacing-quarter) solid rgba(var(--color-neutral-50s));border-radius:var(--border-radius-circle);box-shadow:var(--elevation-x-2) calc(var(--elevation-y-2) - var(--spacing-1) - var(--spacing-quarter)) var(--elevation-blur-2) var(--elevation-spread-2) rgba(var(--elevation-color-2));cursor:grab;height:calc(var(--spacing-half) * 3);left:calc(var(--thumb-pos-x) * (var(--gradient-picker-size) - var(--thumb-size)));overflow:visible;position:absolute;top:calc(var(--thumb-pos-y) * (var(--gradient-picker-size) - var(--thumb-size)));width:calc(var(--spacing-half) * 3)}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker{display:flex;flex-direction:row;height:calc(var(--spacing-1) * 5.5);justify-content:space-between;position:absolute;top:calc(var(--spacing-1) * 24);width:calc(var(--spacing-1) * 25)}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker{align-content:center;display:flex;flex-direction:column;flex-grow:1;height:calc(var(--spacing-1) * 5.5);justify-content:space-between;margin-left:0;width:var(--spacing-4)}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker+div.sc-cl-color-picker{margin-left:var(--spacing-half)}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker input.sc-cl-color-picker{-moz-appearance:textfield;background-color:rgba(var(--color-neutral-900), var(--opacity-1));border-radius:var(--border-radius-interactive-small);border-width:0;color:rgb(var(--font-color-onlight-default));font-family:var(--font-family-ui);font-size:var(--font-size-body-1);height:calc(var(--spacing-1) * 3);padding:0;text-align:center;width:100%}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker input.sc-cl-color-picker::-webkit-outer-spin-button,.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker input.sc-cl-color-picker::-webkit-inner-spin-button{-webkit-appearance:none;margin:0}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker input.sc-cl-color-picker:focus{outline:none}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker input.color-picker__inputs--error.sc-cl-color-picker{border:1px solid rgba(var(--font-color-onlight-error));box-sizing:border-box}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.sc-cl-color-picker cl-typography.sc-cl-color-picker{margin:0;text-align:center}.sc-cl-color-picker-h div.color-picker__inputs.sc-cl-color-picker div.color-picker__inputs--hex.sc-cl-color-picker{flex-grow:2;width:var(--spacing-8)}.color-picker--sliding.sc-cl-color-picker-h{cursor:none}.color-picker--sliding.sc-cl-color-picker-h div.color-picker__gradient-selector.sc-cl-color-picker{cursor:none}.color-picker--sliding.sc-cl-color-picker-h div.color-picker__gradient-selector.sc-cl-color-picker div.color-picker__gradient-selector__thumb.sc-cl-color-picker{cursor:none}.color-picker--sliding.sc-cl-color-picker-h .color-picker__inputs.sc-cl-color-picker input.sc-cl-color-picker{cursor:none}.color-picker--sliding.sc-cl-color-picker-h .color-picker__color-input.sc-cl-color-picker{cursor:none}@keyframes expand{from{opacity:var(--opacity-0, 0)}to{opacity:var(--opacity-5, 1)}}@keyframes collapse{from{opacity:var(--opacity-5, 1)}to{opacity:var(--opacity-0, 0)}}";

const ClColorPicker$1 = /*@__PURE__*/ proxyCustomElement(class ClColorPicker extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clChange = createEvent(this, "clChange", 7);
    this.clCloseEnd = createEvent(this, "clCloseEnd", 7);
    this.clCloseStart = createEvent(this, "clCloseStart", 7);
    this.clOpenEnd = createEvent(this, "clOpenEnd", 7);
    this.clOpenStart = createEvent(this, "clOpenStart", 7);
    this.change = createEvent(this, "change", 7);
    this.closeEnd = createEvent(this, "closeEnd", 7);
    this.closeStart = createEvent(this, "closeStart", 7);
    this.openEnd = createEvent(this, "openEnd", 7);
    this.openStart = createEvent(this, "openStart", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    this.references = {};
    /**
     * The latest HSV value of the color-picker
     */
    this.hsv = [360, 1, 1];
    /**
     * Whether the color-picker is in the process of showing the open/close animation or not
     */
    this.isAnimating = false;
    /**
     * Whether latest mouseEvent started inside the color-picker or not
     */
    this.mouseEventStartedInside = false;
    /**
     * Throttled function for updating the colors state
     */
    this.throttledUpdateColors = throttle((colors) => (this.colors = colors), 50, true);
    /**
     * Handles parsing the given RGB string to an array
     */
    this.rgbStringToArray = (rgbString) => rgbString
      .replace(/[[\s\]]+/g, '')
      .split(',')
      .map(i => +i);
    /**
     * Function that emits the correct animation event based on whether the color-picker is opening or closing
     */
    this.handleAnimationEvent = (event) => {
      const { animationName, type } = event;
      const isOpenEvent = animationName === 'expand';
      const isCloseEvent = animationName === 'collapse';
      switch (true) {
        case isOpenEvent && type === 'animationstart':
          emitEvent(this, 'clOpenStart', { component, originalEvent: event }, null);
          break;
        case isOpenEvent && type === 'animationend':
          this.element.classList.remove('color-picker--opening');
          this.element.classList.add('color-picker--opened');
          this.isAnimating = false;
          emitEvent(this, 'clOpenEnd', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationstart':
          emitEvent(this, 'clCloseStart', { component, originalEvent: event }, null);
          break;
        case isCloseEvent && type === 'animationend':
          this.element.classList.remove('color-picker--closing');
          this.element.classList.add('color-picker--closed');
          this.isAnimating = false;
          emitEvent(this, 'clCloseEnd', { component, originalEvent: event }, null);
          break;
      }
    };
    /**
     * Handles setting isDraggingGradientThumb to false when the user stops dragging the gradient thumb
     */
    this.onGradientDragStop = (event) => {
      this.updateHSV(event);
      this.isDraggingGradientThumb = false;
    };
    /**
     * Handles setting isDraggingGradientThumb to true when the user starts dragging the gradient thumb
     */
    this.onGradientDragStart = (event) => {
      event.preventDefault();
      this.isDraggingGradientThumb = true;
      window.addEventListener('mouseup', this.onGradientDragStop, { once: true });
    };
    /**
     * Handles setting the ref for the given element
     */
    this.setRef = (ref) => (element) => {
      this.references[ref] = element;
    };
    /**
     * Handles updating the colors state based on the position of the thumb
     * on the gradient OR based on the given HSV array
     */
    this.updateHSV = (event) => {
      if (Object.keys(this.references).length) {
        const isEvent = !Array.isArray(event);
        let hsv = isEvent ? [...this.hsv] : event;
        let hex = hsvToHex(hsv);
        if (isEvent) {
          if (event.type.includes('mouse')) {
            if (!this.isDraggingGradientThumb)
              return;
            // TODO: Fix sliding from breaking when there is browser zoom
            const thumbSize = this.references.gradientThumb.offsetWidth;
            const picker = this.references.gradient.getBoundingClientRect();
            const pickerSize = picker.width - thumbSize;
            const posX = clamp(event.x - picker.x - thumbSize / 2, 0, pickerSize);
            const posY = clamp(event.y - picker.y - thumbSize / 2, 0, pickerSize);
            hsv = [hsv[0], posX / pickerSize, 1 - posY / pickerSize];
          }
          else {
            hsv[0] = Number.parseFloat(this.references.slider.value);
          }
          hex = hsvToHex(hsv);
          this.throttledUpdateColors({ hex, rgb: hsvToRgb(hsv) });
        }
        this.hsv = hsv;
        const [r, g, b] = hsvToRgb(hsv);
        this.references.slider.style.setProperty('--picker-hue', `${hsv[0]}`);
        this.references.slider.value = `${hsv[0]}`;
        this.references.gradient.style.setProperty('--picker-hue', `${hsv[0]}`);
        this.references.gradientThumb.style.setProperty('--thumb-pos-x', `${hsv[1]}`);
        this.references.gradientThumb.style.setProperty('--thumb-pos-y', `${1 - hsv[2]}`);
        this.references.gradientThumb.style.setProperty('--thumb-colour', hex);
        this.references.rgbInputR.value = `${r}`;
        this.references.rgbInputG.value = `${g}`;
        this.references.rgbInputB.value = `${b}`;
        if (this.references.hexInput) {
          this.references.hexInput.value = hex;
        }
      }
    };
    /**
     * Handles updating the colors state when the given HEX value is valid
     */
    this.updateHEX = (event) => {
      var _a;
      const isEvent = typeof event === 'object';
      if (!this.isDraggingGradientThumb || (this.isDraggingGradientThumb && isEvent)) {
        const hex = isEvent ? event.target.value : event;
        const isValid = isValidHex(hex);
        (_a = this.references.hexInput) === null || _a === void 0 ? void 0 : _a.classList.toggle('color-picker__inputs--error', !isValid);
        if (!isValid) {
          return;
        }
        if (isEvent) {
          const composedPath = event.composedPath();
          if (composedPath && composedPath.includes(this.references.hexInput)) {
            this.throttledUpdateColors({ hex, rgb: hexToRgb(hex) });
          }
        }
        this.updateHSV(hexToHsv(hex));
      }
    };
    /**
     * Handles updating the colors state when the given RGB value is valid
     */
    this.updateRGB = (event) => {
      const isEvent = !Array.isArray(event);
      if (isEvent && !isNumber(event.target.value)) {
        return;
      }
      if (!this.isDraggingGradientThumb || (this.isDraggingGradientThumb && isEvent)) {
        const rgb = isEvent ? [...this.colors.rgb] : event;
        if (isEvent) {
          const target = event.target;
          const index = target.getAttribute('data-index');
          if (index !== null) {
            rgb[+index] = clamp(Number.parseInt(target.value), 0, 255);
            const composedPath = event.composedPath();
            if (!composedPath.includes(this.references.rgbInputR) ||
              !composedPath.includes(this.references.rgbInputG) ||
              !composedPath.includes(this.references.rgbInputB)) {
              this.throttledUpdateColors({ hex: rgbToHex(rgb), rgb });
            }
          }
        }
        if (!isValidRgb(rgb)) {
          this.logger.error.attribute.rgb(`Given invalid value "${rgb}".`);
          return;
        }
        this.updateHSV(rgbToHsv(rgb));
      }
    };
    this.colors = { hex: '#FF0000', rgb: [255, 0, 0] };
    this.isDraggingGradientThumb = false;
    this.autoClose = true;
    this.displayHex = false;
    this.hex = '#FF0000';
    this.open = false;
    this.rgb = '[255, 0, 0]';
  }
  /**
   * Handles updating the HEX/RGB props and emitting the change
   * when the colors state has been updated
   */
  handleColorsChange(colors, previousColors) {
    const [rgb, previousRgb] = [`${colors.rgb}`, `${previousColors.rgb}`];
    const hexChanged = colors.hex !== previousColors.hex && colors.hex !== this.hex;
    const rgbChanged = rgb !== previousRgb && rgb !== `${this.rgbStringToArray(this.rgb)}`;
    if (hexChanged) {
      this.hex = colors.hex;
    }
    if (rgbChanged) {
      this.rgb = `[${rgb}]`;
    }
    if (hexChanged || rgbChanged) {
      emitEvent(this, 'clChange', {
        component,
        rgb: colors.rgb,
        hex: colors.hex,
        hsv: this.hsv,
      }, { rgb: colors.rgb, hex: colors.hex, hsv: this.hsv });
    }
  }
  /**
   * Updates the colors state when the hex value has changed.
   */
  handleHexChange(hex) {
    if (hex !== this.colors.hex) {
      this.updateHEX(hex);
    }
  }
  /**
   * Updates the cl-color-picker element styles based on the `open` property
   */
  handleOpen(isOpen, wasOpen) {
    switch (true) {
      case isOpen && !wasOpen: {
        this.element.classList.remove('color-picker--closing');
        this.element.classList.remove('color-picker--closed');
        this.element.classList.add('color-picker--opening');
        this.isAnimating = true;
        break;
      }
      case !isOpen && wasOpen: {
        this.element.classList.remove('color-picker--opening');
        this.element.classList.remove('color-picker--opened');
        this.element.classList.add('color-picker--closing');
        this.isAnimating = true;
        break;
      }
      case !isOpen && !wasOpen: {
        this.element.classList.remove('color-picker--opened');
        this.element.classList.add('color-picker--closed');
        break;
      }
    }
  }
  /**
   * Updates the colour picker if the RGB values changes
   */
  handleRgbChange(rgbString) {
    const rgb = this.rgbStringToArray(rgbString);
    if (`${rgb}` !== `${this.colors.rgb}`) {
      this.updateRGB(rgb);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Apply the open/close styling;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'change', 'clChange');
    logDeprecatedEventOnce(this.logger, 'closeEnd', 'clCloseEnd');
    logDeprecatedEventOnce(this.logger, 'closeStart', 'clCloseStart');
    logDeprecatedEventOnce(this.logger, 'openEnd', 'clOpenEnd');
    logDeprecatedEventOnce(this.logger, 'openStart', 'clOpenStart');
    this.handleOpen(this.open);
  }
  /**
   * Just once after the component has fully loaded and rendered:
   * - Validate and apply initial RGB attribute;
   * - If display-hex is true, validate and apply initial HEX attribute;
   */
  componentDidLoad() {
    this.handleRgbChange(this.rgb);
    if (this.displayHex) {
      this.handleHexChange(this.hex);
    }
  }
  /**
   * Handles clicks outside the colour picker
   */
  handleOutsideClick(event) {
    if (this.open) {
      const isOutsideClick = !event.composedPath().includes(this.element);
      if (!this.isDraggingGradientThumb &&
        !this.isAnimating &&
        isOutsideClick &&
        !this.mouseEventStartedInside &&
        this.autoClose) {
        this.open = false;
      }
    }
  }
  /**
   * Check if mousedowns are in or outside the colour picker
   */
  handleMouseDown(event) {
    const isInsideClick = event.composedPath().includes(this.element);
    this.mouseEventStartedInside = isInsideClick;
  }
  /**
   * Move the gradient thumb if the mouse is moving
   */
  handlePickUpdate(event) {
    if (this.isDraggingGradientThumb) {
      event.preventDefault();
      this.updateHSV(event);
    }
  }
  /**
   * Ends the gradient thumb movement if mouseup
   */
  handleStopPicking(event) {
    if (this.isDraggingGradientThumb) {
      this.onGradientDragStop(event);
    }
  }
  render() {
    const { displayHex } = this;
    return (h(Host, { class: cl('color-picker', {
        ['color-picker--sliding']: this.isDraggingGradientThumb,
      }), onAnimationEnd: this.handleAnimationEvent, onAnimationStart: this.handleAnimationEvent }, h("div", { class: 'color-picker__content' }, h("input", { ref: this.setRef('slider'), class: 'color-picker__color-input', max: 360, min: 0, type: 'range', value: 360, onFocus: this.updateHSV, onInput: this.updateHSV }), h("div", { ref: this.setRef('gradient'), class: 'color-picker__gradient-selector', onClick: this.onGradientDragStop, onMouseDown: this.onGradientDragStart }, h("div", { ref: this.setRef('gradientThumb'), class: 'color-picker__gradient-selector__thumb' })), h("div", { class: 'color-picker__inputs' }, displayHex ? (h("div", { class: 'color-picker__inputs--hex' }, h("input", { ref: this.setRef('hexInput'), value: `${this.colors.hex}`, onInput: this.updateHEX }), h("cl-typography", { type: 'disabled', variant: 'body-2' }, "HEX"))) : undefined, h("div", null, h("input", { ref: this.setRef('rgbInputR'), "data-index": 0, max: 255, min: 0, step: 1, type: 'number', value: this.colors.rgb[0], onInput: this.updateRGB }), h("cl-typography", { type: 'disabled', variant: 'body-2' }, "R")), h("div", null, h("input", { ref: this.setRef('rgbInputG'), "data-index": 1, max: 255, min: 0, step: 1, type: 'number', value: this.colors.rgb[1], onInput: this.updateRGB }), h("cl-typography", { type: 'disabled', variant: 'body-2' }, "G")), h("div", null, h("input", { ref: this.setRef('rgbInputB'), "data-index": 2, max: 255, min: 0, step: 1, type: 'number', value: this.colors.rgb[2], onInput: this.updateRGB }), h("cl-typography", { type: 'disabled', variant: 'body-2' }, "B"))))));
  }
  get element() { return this; }
  static get watchers() { return {
    "colors": ["handleColorsChange"],
    "hex": ["handleHexChange"],
    "open": ["handleOpen"],
    "rgb": ["handleRgbChange"]
  }; }
  static get style() { return clColorPickerCss; }
}, [2, "cl-color-picker", {
    "autoClose": [4, "auto-close"],
    "displayHex": [4, "display-hex"],
    "hex": [1537],
    "open": [1540],
    "rgb": [1537],
    "colors": [32],
    "isDraggingGradientThumb": [32]
  }, [[6, "click", "handleOutsideClick"], [5, "mousedown", "handleMouseDown"], [5, "mousemove", "handlePickUpdate"], [5, "mouseup", "handleStopPicking"]]]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-color-picker", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-color-picker":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClColorPicker$1);
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const ClColorPicker = ClColorPicker$1;
const defineCustomElement = defineCustomElement$1;

export { ClColorPicker, defineCustomElement };

//# sourceMappingURL=cl-color-picker.js.map